import React from 'react';

import { aiConfigItemMessages } from '@atlaskit/editor-common/ai-messages';
import { AICommandIcon } from '@atlaskit/editor-toolbar';

import { FixSpellingIcon } from '../../../../icons/prompt/fixSpelling';
import { ImproveWritingIcon } from '../../../../icons/prompt/improveWriting';
import type { ConvoAiFeatureId } from '../../../../utils/request/types';
import { createInsertBelow, createReplace } from '../../actions/create-actions';
import { CONFIG_ITEM_KEYS } from '../../config-item-keys';
import type {
	EditorPluginAIConfigItemFactory,
	EditorPluginAIConfigItemWithOptionsFactory,
} from '../../config-items';

export const improveWriting: EditorPluginAIConfigItemFactory = (options) => {
	const featureId: ConvoAiFeatureId = 'improve_writing_adf_gpt41mini';

	return {
		key: CONFIG_ITEM_KEYS.IMPROVE_WRITING,
		title: aiConfigItemMessages.improveWritingTitle,
		description: aiConfigItemMessages.improveWritingDescription,
		icon: ({ formatMessage }) =>
			options?.isInDocumentStreamingEnabled ? (
				<AICommandIcon
					label={formatMessage(aiConfigItemMessages.improveWritingTitle)}
					size="small"
				/>
			) : (
				<ImproveWritingIcon />
			),
		intentSchemaId: 'improve_quality_intent_schema.json',
		featureId,
		adfPrompt: true,
		requiresTextualContent: true,
		actions: {
			empty: [
				createInsertBelow({ appearance: 'secondary', factoryOptions: options }),
				createReplace({ appearance: 'primary' }),
			],
			selection: [
				createInsertBelow({ appearance: 'secondary', factoryOptions: options }),
				createReplace({ appearance: 'primary' }),
			],
		},
		getBackendModel: () => {
			return 'assistance-service';
		},
		canToggleDiffPreview: true,
	};
};

export const improveWritingWithOptions: EditorPluginAIConfigItemWithOptionsFactory = (options) => ({
	config: improveWriting(options),
	triggers: { empty: { enabled: true, docMinSize: 1 }, selection: { enabled: true } },
});

export const fixSpellingAndGrammar: EditorPluginAIConfigItemFactory = () => {
	const featureId: ConvoAiFeatureId = 'fix_spelling_grammar_adf_gpt41mini';

	return {
		key: CONFIG_ITEM_KEYS.FIX_SPELLING_AND_GRAMMAR,
		title: aiConfigItemMessages.fixSpellingGrammarTitle,
		description: aiConfigItemMessages.fixSpellingGrammarDescription,
		icon: () => <FixSpellingIcon />,
		intentSchemaId: 'grammar_spelling_intent_schema.json',
		featureId,
		adfPrompt: true,
		requiresTextualContent: true,
		actions: {
			empty: [createReplace({ appearance: 'primary' })],
			selection: [createReplace({ appearance: 'primary' })],
		},
		getBackendModel: () => {
			return 'assistance-service';
		},
	};
};

export const fixSpellingAndGrammarWithOptions: EditorPluginAIConfigItemWithOptionsFactory = (
	options,
) => ({
	config: fixSpellingAndGrammar(options),
	triggers: { empty: { enabled: true, docMinSize: 1 }, selection: { enabled: true } },
});
